// Browser projection of the workbook number formats defined by
// core/xlm/src/xlm/units.py. Keep this map aligned with that renderer-owned
// source of truth so the model grid and downloaded Excel workbook agree. The
// grid uses the dollar sign for USD and explicit ISO labels for other reporting
// currencies so non-USD models cannot be mislabeled.

const FRACTION_DIGITS: Readonly<Record<string, number>> = {
  currency_b: 1,
  currency_mm: 0,
  currency_k: 0,
  currency_unit: 2,
  units_b: 1,
  units_mm: 1,
  units_k: 0,
  units_unit: 0,
  shares_mm: 1,
  shares_k: 0,
  shares: 0,
  percent: 1,
};

const UNIT_LABELS: Readonly<Record<string, string>> = {
  currency_b: "$B",
  currency_mm: "$M",
  currency_k: "$K",
  currency_unit: "$",
  units_b: "B. Units",
  units_mm: "M. Units",
  units_k: "K Units",
  units_unit: "Units",
  shares_mm: "M shares",
  shares_k: "K shares",
  shares: "shares",
  percent: "%",
  boolean: "Boolean",
};

const CURRENCY_SCALE_LABELS: Readonly<Record<string, string>> = {
  currency_b: "B",
  currency_mm: "M",
  currency_k: "K",
  currency_unit: "",
};

const formatters = new Map<number, Intl.NumberFormat>();

function formatter(fractionDigits: number): Intl.NumberFormat {
  const existing = formatters.get(fractionDigits);
  if (existing) return existing;
  const created = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  formatters.set(fractionDigits, created);
  return created;
}

function normalizedUnit(unit: string | null | undefined): string {
  const normalized = unit?.trim().toLocaleLowerCase() ?? "";
  if (normalized === "%") return "percent";
  return normalized;
}

/** Match the public unit labels emitted by financial-export/v2. */
export function displayXLMUnit(
  unit: string | null | undefined,
  explicitLabel: string | null | undefined,
  reportingCurrency?: string | null,
): string {
  if (explicitLabel?.trim()) return explicitLabel.trim();
  const normalized = normalizedUnit(unit);
  const currency = reportingCurrency?.trim().toLocaleUpperCase() ?? "";
  const currencyScale = CURRENCY_SCALE_LABELS[normalized];
  if (/^[A-Z]{3}$/.test(currency) && currencyScale !== undefined) {
    const currencyLabel = currency === "USD" ? "$" : currency;
    if (currencyScale === "") return currencyLabel;
    return currency === "USD"
      ? `${currencyLabel}${currencyScale}`
      : `${currencyScale}. ${currencyLabel}`;
  }
  return UNIT_LABELS[normalized] ?? unit?.trim() ?? "—";
}

/** Match the XLM-to-XLSX renderer's display rules for a model value. */
export function formatXLMNumber(
  value: number,
  unit: string | null | undefined,
  options: { zeroStyle?: "dash" | "number" } = {},
): string {
  if (!Number.isFinite(value)) return "–";

  const normalized = normalizedUnit(unit);
  if (normalized === "multiple" || normalized === "x") {
    return `${formatter(1).format(value)}x`;
  }
  if (value === 0 && options.zeroStyle !== "number") return "–";

  const percent = normalized === "percent";
  const displayValue = percent ? value * 100 : value;
  const fractionDigits = FRACTION_DIGITS[normalized] ?? 0;
  const formatted = formatter(fractionDigits).format(Math.abs(displayValue));
  const withUnit = percent ? `${formatted}%` : formatted;
  return displayValue < 0 ? `(${withUnit})` : withUnit;
}
